import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useState} from 'react'
import type {PendingComment} from '../../page-data/payloads/thread-previews'
import {ConversationHeader} from './ConversationHeader'
import {PreviewAuthors} from './PreviewAuthors'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useNavigateToComment, usePullRequestBasePath} from '../../stores/layout-selectors'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useNavigate} from '@github-ui/use-navigate'
import styles from './PendingCommentPreview.module.css'

interface PendingCommentPreviewProps {
  pendingComment: PendingComment
  onNavigateToDiffComment?: (threadId: string) => void
  tabSize?: number
}

export const PendingCommentPreview = memo(function PendingCommentPreviewComponent({
  onNavigateToDiffComment,
  pendingComment,
  tabSize,
}: PendingCommentPreviewProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const {commentID, thread} = pendingComment
  const navigateToSelectedComment = useNavigateToComment()
  const basePath = usePullRequestBasePath()
  const navigate = useNavigate()
  const previousCommentCount = thread.commentsData?.comments?.length ? thread.commentsData.comments.length - 1 : 0
  const previousCommentText = `${previousCommentCount} previous ${previousCommentCount === 1 ? 'comment' : 'comments'}`

  const navigateToComment = useCallback(() => {
    if (navigateToSelectedComment) {
      navigateToSelectedComment(commentID)
      const currentUrl = window.location.pathname + window.location.search
      addUrlToHistoryStack(`${currentUrl}#r${commentID}`)
    } else {
      navigate(`${basePath}/changes#r${commentID}`)
    }
    onNavigateToDiffComment?.(commentID.toString())
  }, [commentID, navigateToSelectedComment, basePath, navigate, onNavigateToDiffComment])

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ConversationHeader
        isCollapsed={isCollapsed}
        isOutdated={thread.isOutdated || false}
        isResolved={thread.isResolved || false}
        line={thread.subject?.endLine}
        path={thread.path ?? ''}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(collapsed => !collapsed)}
        threadId={thread.id}
        firstCommentId={commentID}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={thread.subject?.diffLines}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          {previousCommentCount > 0 && (
            <div className="my-2 px-2">
              <Button
                aria-label="View comment in diff"
                size="small"
                trailingVisual={ChevronRightIcon}
                variant="invisible"
                onClick={navigateToComment}
              >
                <div className="d-flex flex-row flex-justify-start flex-items-center gap-2">
                  <span>{previousCommentText}</span>
                  <PreviewAuthors skipAfterCommentId={commentID} thread={thread} />
                </div>
              </Button>
            </div>
          )}
          <VerifiedHTMLDiv
            className={clsx('markdown-body', styles.commentBody)}
            html={extractCommentBodyHTML(pendingComment)}
          />
        </>
      )}
    </div>
  )
})

function extractCommentBodyHTML(pendingComment: PendingComment): SafeHTMLString {
  const commentsArray = pendingComment.thread.commentsData?.comments ?? []
  const matchedComment = commentsArray.find(comment => comment && comment.databaseId === pendingComment.commentID)
  const html = matchedComment?.bodyHTML ?? ''

  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
  return html as SafeHTMLString
}
